import React from 'react';
import {
	Alert,
	Flex,
	Space,
	Tooltip,
	Typography,
} from 'antd';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faStar,
	faWrench,
} from '@fortawesome/pro-light-svg-icons';
import {
	Icon,
	IconProp,
} from '@fortawesome/fontawesome-svg-core';
import {
	faArrowsRotate,
	faStar as faStarSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { useQueryClient } from 'react-query';
import {
	Currencies,
	DashboardPeriodLabels,
	DashboardPeriods,
	DashboardTypes,
	DATE_AND_TIME,
	DepartmentKey,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import {
	nowMoment,
	toMoment,
} from '@shared/utils/date';
import SimpleScreen from '@client/components/screens/SimpleScreen';
import { useAuth } from '@client/lib/auth';
import TooltipIcon from '@client/components/TooltipIcon';
import { updateUser } from '@client/lib/api';
import Button from '@client/components/Button';
import Select from '@client/components/Select';
import styles from './HomeScreen.module.css';
import DashboardInfoProvider, { useDashboardCurrency } from './helpers/dashboardInfo';
import getDepartmentDashboards from './helpers/getDepartmentDashboards';

const HomeScreen = () => {
	const { userInfo, refreshAuthInfo } = useAuth();
	const queryClient = useQueryClient();
	let dashboards = getDepartmentDashboards(
		userInfo.activeDepartment as DepartmentKey,
		queryClient,
	);

	if (userInfo.organizationName === 'Alba Tankers') {
		dashboards = dashboards.filter((d) => d.key !== DashboardTypes.OUTSTANDINGS);
	}

	const setDefaultDashboard = async (entry: Values<typeof DashboardTypes>) => {
		if (entry != null) {
			await updateUser({
				attributes: {
					defaultDashboard: entry,
				},
			});

			await refreshAuthInfo();
		}
	};

	const DashboardOption = <T,>({
		label,
		selectedEntry,
		entry,
		defaultable,
		onClick,
	}: {
		label: string;
		selectedEntry: T;
		entry: T;
		defaultable?: boolean;
		onClick: (p: T) => void;
	}) => {
		return (
			<button
				type="button"
				onClick={() => onClick(entry)}
				className={classNames(styles.dashboardEntry, {
					[styles.activeEntry]: entry === selectedEntry,
				})}
			>
				{label}
				{defaultable && entry === selectedEntry && (
					<Tooltip title={`Set ${label} as your default tab`}>
						{' '}
						<FontAwesomeIcon
							onClick={async (e) => {
								e.preventDefault();
								e.stopPropagation();
								await setDefaultDashboard(entry as Values<typeof DashboardTypes>);
							}}
							icon={selectedEntry === userInfo.defaultDashboard ? faStarSolid : faStar as IconProp}
							size="xs"
						/>
					</Tooltip>
				)}
			</button>
		);
	};

	const DashboardPeriodSelect = ({
		selectedDashboardKey,
		periodOptions,
		period,
		setPeriod,
	}: {
		selectedDashboardKey: Values<typeof DashboardTypes>;
		periodOptions?: Array<Values<typeof DashboardPeriods>>;
		period: Values<typeof DashboardPeriods>;
		setPeriod: (p: Values<typeof DashboardPeriods>) => void;
	}) => {
		if (!periodOptions?.length) {
			return null;
		}

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
				<Typography.Text className={styles.smallText} type="secondary">
					{selectedDashboardKey === DashboardTypes.AGED_RECEIVABLES ? 'Aged up to' : 'Period'}
				</Typography.Text>

				<Space>
					{periodOptions.map((p) => (
						<DashboardOption<Values<typeof DashboardPeriods>>
							key={p}
							onClick={setPeriod}
							label={DashboardPeriodLabels[p]}
							selectedEntry={period}
							entry={p}
						/>
					))}
				</Space>
			</div>
		);
	};

	const DashboardCurrencySelect = ({ enabled }: { enabled: boolean }) => {
		const currencyCtx = useDashboardCurrency();

		if (!enabled) {
			return null;
		}

		if (!currencyCtx) {
			return null;
		}

		const { currency, setCurrency, availableCurrencies } = currencyCtx;

		if (!availableCurrencies.length) {
			return null;
		}

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
				<Typography.Text className={styles.smallText} type="secondary">
					Currency
				</Typography.Text>

				<Space>
					<Select
						style={{ minWidth: 80 }}
						options={availableCurrencies.map((c) => ({ label: c, value: c }))}
						value={currency ?? availableCurrencies[0]}
						onChange={(v) => setCurrency(v as Values<typeof Currencies>)}
						showSearch
					/>
				</Space>
			</div>
		);
	};

	const filterCheck = () => {
		const savedTime = localStorage.getItem('vesselFilterAlert');

		if (savedTime == null) {
			return true;
		}

		return nowMoment().isAfter(toMoment(savedTime));
	};

	return (
		<DashboardInfoProvider
			key={`${userInfo.organizationName}-${userInfo.activeDepartment}`} // or org.id
			dashboards={dashboards}>
			{({
				enableCache = true,
				period,
				setPeriod,
				DashboardComponent,
				selectedDashboard,
				setDashboard,
				options,
				lastUpdated,
				setLastUpdated,
			}) => {
				const hasPeriodOptions = selectedDashboard.periodOptions != null;
				const showCurrencySelect = selectedDashboard.enableCurrencyFilter === true;

				return (
					<SimpleScreen
						reducedPadding
						rootPageTitle="Home"
						headerOverride={(
							<Flex justify="space-between" className={styles.header}>
								<div>
									<Space>
										<div style={{ display: 'flex', flexDirection: 'column' }}>
											<Typography.Text className={styles.smallText} type="secondary">
												Dashboard
											</Typography.Text>
											<Space>
												{dashboards.map((dashboard) => (
													<DashboardOption<Values<typeof DashboardTypes>>
														key={dashboard.key}
														onClick={setDashboard}
														label={dashboard.label}
														selectedEntry={selectedDashboard.key}
														entry={dashboard.key}
														defaultable
													/>
												))}
											</Space>
										</div>
									</Space>
									<div style={{ marginTop: 10 }}>
										<div style={
											{
												display: 'flex',
												alignItems: 'flex-start',
												gap: 24,
												flexWrap: 'wrap'
											}}>
											<DashboardCurrencySelect enabled={showCurrencySelect} />
											<DashboardPeriodSelect
												selectedDashboardKey={selectedDashboard.key}
												periodOptions={selectedDashboard.periodOptions}
												period={period}
												setPeriod={setPeriod}
											/>
											{hasPeriodOptions && !(userInfo.filterVessels && filterCheck()) && (
												<div style={{ alignSelf: 'center', paddingTop: 20 }}>
													<TooltipIcon className={styles.periodTooltip}>
														<span>
															The stats below are based on your default vessel filter.
															To change or disable this filter, go to{' '}
															<FontAwesomeIcon icon={faWrench as Icon} /> {' -> '} Vessels.
														</span>
													</TooltipIcon>
												</div>
											)}
										</div>
									</div>
								</div>
								{enableCache && (
									<Flex
										vertical
										className={styles.refreshContainer}
									>
										<Typography.Text className={styles.smallText} type="secondary">
											Last updated:
											{' '}
											{lastUpdated?.local().format(DATE_AND_TIME)}
										</Typography.Text>
										<Button
											onClick={() => selectedDashboard?.refresh(selectedDashboard.key, period)}
											className={classNames([styles.activeEntry, styles.refreshBtn])}
											icon={(
												<FontAwesomeIcon icon={faArrowsRotate} />
											)}
										>
											Refresh data
										</Button>
									</Flex>
								)}
							</Flex>
						)}
					>
						{(userInfo.filterVessels && filterCheck()) ? (
							<Alert
								className={styles.vesselFilterAlert}
								message={(
									<span>
										The stats below are based on your default vessel filter.
										To change or disable this filter, go to
										{' '}
										<FontAwesomeIcon
											icon={faWrench as Icon}
										/>
										{' -> '}
										Vessels.
									</span>
								)}
								closable
								onClose={() => {
									localStorage.setItem(
										'vesselFilterAlert',
										nowMoment().add(1, 'month').toISOString(),
									);
								}}
							/>
						) : null}
						<DashboardComponent
							options={options}
							setLastUpdated={setLastUpdated}
							period={period}
						/>
					</SimpleScreen>
				);
			}}
		</DashboardInfoProvider>
	);
};

export default HomeScreen;
